// ============================================================
// ONBOARDING — first-visit tutorial overlay for the design
// editor, ported from the sandbox prototype's onboarding.ts.
// Button lookups are scoped to .design-editor so the generic
// .btn class can't match anything outside the editor.
// ============================================================

const LS_KEY = 'resume-onboarding-seen'

/**
 * What a tour step can select on the canvas. The sandbox selected its
 * default template's hardcoded component ids; production designs have
 * arbitrary ids, so steps select the first component of a given type
 * instead. 'any-layerable' picks any component that shows the z-order bar
 * (i.e. not the resume block or the watermark). Selecting nothing when the
 * design has no such component is fine.
 */
export type OnboardingComponentType =
  | 'text'
  | 'container'
  | 'image'
  | 'resume'
  | 'any-layerable'

export interface OnboardingActions {
  selectComponentByType: (type: OnboardingComponentType) => void
  deselectAll: () => void
}

interface Step {
  title: string
  body: string
  icon: string
  highlights?: {
    buttonText?: string | string[]
    componentType?: OnboardingComponentType
    selector?: string
  }
}

const STEPS: Step[] = [
  {
    title: 'Welcome to the Designer',
    body: 'Design custom headers and footers that wrap around any resume. This quick tour shows you the basics — or skip ahead and jump right in.',
    icon: '<div style="font-size:48px;line-height:1;">📄</div>',
  },
  {
    title: 'Text Blocks',
    body: 'Click <b>+ Text Block</b> to add editable text. Once placed, click it to open the formatting toolbar — change fonts, sizes, colors, alignment, and more.',
    icon: `<div style="display:flex;align-items:center;gap:10px;">
      <div style="width:48px;height:48px;border-radius:8px;background:var(--color-teal-600);color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:600;">Aa</div>
      <div style="display:flex;flex-direction:column;gap:3px;">
        <div style="width:80px;height:6px;background:#cbd5e1;border-radius:3px;"></div>
        <div style="width:60px;height:6px;background:#cbd5e1;border-radius:3px;"></div>
      </div>
    </div>`,
    highlights: { buttonText: '+ Text Block', componentType: 'text' },
  },
  {
    title: 'Boxes',
    body: 'Click <b>+ Box</b> to add a shape with customizable background, borders, and corner radius. Great for colored bands, dividers, and framing.',
    icon: `<div style="width:80px;height:48px;border-radius:8px;border:3px solid var(--color-teal-600);background:var(--color-teal-50);position:relative;">
      <div style="position:absolute;top:4px;left:4px;right:4px;height:4px;background:color-mix(in srgb, var(--color-teal-600) 25%, transparent);border-radius:2px;"></div>
      <div style="position:absolute;bottom:4px;left:4px;width:60%;height:4px;background:color-mix(in srgb, var(--color-teal-600) 25%, transparent);border-radius:2px;"></div>
    </div>`,
    highlights: { buttonText: '+ Box', componentType: 'container' },
  },
  {
    title: 'Images',
    body: 'Click <b>+ Image</b> to add a logo or graphic. Drop an image file onto it, or click the placeholder to browse. Corner handles preserve aspect ratio.',
    icon: '<div style="width:56px;height:56px;border-radius:8px;background:#f0f4f8;border:2px dashed #94a3b8;display:flex;align-items:center;justify-content:center;font-size:24px;">🖼</div>',
    highlights: { buttonText: '+ Image', componentType: 'image' },
  },
  {
    title: 'The Resume Area',
    body: 'The grey mockup in the center represents the actual resume content. <b>Resize it</b> so your header and footer fill the remaining space around it. It always stays behind your components.',
    icon: `<div style="width:50px;height:64px;border-radius:4px;background:#f1f1f1;position:relative;box-shadow:0 1px 4px rgba(0,0,0,.12);">
      <div style="position:absolute;top:3px;left:3px;right:3px;height:10px;background:color-mix(in srgb, var(--color-teal-600) 19%, transparent);border-radius:2px;"></div>
      <div style="position:absolute;top:16px;left:5px;right:5px;display:flex;flex-direction:column;gap:2px;">
        <div style="height:2px;background:#ddd;border-radius:1px;width:90%;"></div>
        <div style="height:2px;background:#ddd;border-radius:1px;width:75%;"></div>
        <div style="height:2px;background:#ddd;border-radius:1px;width:85%;"></div>
        <div style="height:2px;background:#ddd;border-radius:1px;width:60%;"></div>
      </div>
      <div style="position:absolute;bottom:3px;left:3px;right:3px;height:6px;background:color-mix(in srgb, var(--color-teal-600) 19%, transparent);border-radius:2px;"></div>
    </div>`,
    highlights: { componentType: 'resume' },
  },
  {
    title: 'Layering (Z-Order)',
    body: 'Components can overlap. When you select one, the <b>layer controls</b> appear below it — use them to send a component forward, backward, to the front, or to the back. The resume placeholder always stays at the very bottom.',
    icon: `<div style="position:relative;width:80px;height:56px;">
      <div style="position:absolute;left:0;top:20px;width:50px;height:30px;background:var(--color-teal-50);border:2px solid var(--color-teal-600);border-radius:4px;"></div>
      <div style="position:absolute;left:20px;top:8px;width:50px;height:30px;background:#fff3e0;border:2px solid #b45a2a;border-radius:4px;"></div>
      <div style="position:absolute;left:0;bottom:0;display:flex;gap:2px;">
        <div style="width:14px;height:10px;background:#1e293b;border-radius:2px;display:flex;align-items:center;justify-content:center;">
          <div style="width:8px;height:2px;background:#e2e8f0;border-radius:1px;"></div>
        </div>
        <div style="width:14px;height:10px;background:#1e293b;border-radius:2px;display:flex;align-items:center;justify-content:center;">
          <div style="width:8px;height:2px;background:#e2e8f0;border-radius:1px;"></div>
        </div>
        <div style="width:14px;height:10px;background:#334155;border-radius:2px;display:flex;align-items:center;justify-content:center;">
          <div style="width:8px;height:2px;background:#94a3b8;border-radius:1px;"></div>
        </div>
        <div style="width:14px;height:10px;background:#334155;border-radius:2px;display:flex;align-items:center;justify-content:center;">
          <div style="width:8px;height:2px;background:#94a3b8;border-radius:1px;"></div>
        </div>
      </div>
    </div>`,
    highlights: {
      componentType: 'any-layerable',
      selector: '.z-order-bar',
    },
  },
  {
    title: 'Watermark',
    body: 'Toggle the <b>Watermark</b> on to add text like "CONFIDENTIAL" that overlays the page. Edit it just like any text block — change the wording, font, color, and position. Design it here assuming it stays on; later you can enable or disable it per workflow.',
    icon: `<div style="position:relative;width:70px;height:56px;">
      <div style="width:50px;height:56px;border-radius:4px;background:#f8f8f8;border:1px solid #ddd;display:flex;flex-direction:column;gap:2px;padding:4px;">
        <div style="height:2px;background:#ddd;border-radius:1px;width:90%;"></div>
        <div style="height:2px;background:#ddd;border-radius:1px;width:70%;"></div>
        <div style="height:2px;background:#ddd;border-radius:1px;width:80%;"></div>
      </div>
      <div style="position:absolute;left:8px;top:16px;font-size:14px;font-weight:bold;color:rgba(220,38,38,0.3);transform:rotate(-30deg);white-space:nowrap;">DRAFT</div>
    </div>`,
    highlights: { buttonText: ['Watermark Off', 'Watermark On'] },
  },
  {
    title: 'Apply Design To',
    body: 'Choose how your design is applied across pages. <b>All Pages</b> uses the same design everywhere. <b>First Page Only</b> applies it to just page one. <b>First & Subsequent Are Different</b> lets you design a separate layout for the first page and remaining pages — tabs will appear above the canvas.',
    icon: `<div style="display:flex;flex-direction:column;gap:4px;align-items:center;">
      <div style="display:flex;gap:3px;">
        <div style="width:20px;height:26px;border-radius:2px;background:var(--color-teal-600);display:flex;align-items:center;justify-content:center;">
          <div style="width:12px;height:18px;border-radius:1px;background:white;opacity:0.3;"></div>
        </div>
        <div style="width:20px;height:26px;border-radius:2px;background:var(--color-teal-600);display:flex;align-items:center;justify-content:center;">
          <div style="width:12px;height:18px;border-radius:1px;background:white;opacity:0.3;"></div>
        </div>
        <div style="width:20px;height:26px;border-radius:2px;background:var(--color-teal-600);display:flex;align-items:center;justify-content:center;">
          <div style="width:12px;height:18px;border-radius:1px;background:white;opacity:0.3;"></div>
        </div>
      </div>
      <div style="font-size:8px;color:var(--color-teal-600);font-weight:600;">ALL PAGES</div>
    </div>`,
    highlights: { buttonText: 'All Pages' },
  },
  {
    title: 'Page Size & Grid',
    body: 'Switch between <b>Letter</b> and <b>A4</b> in the right panel. Toggle the <b>grid</b> for alignment guides, and enable <b>snap</b> for precision placement.',
    icon: `<div style="display:flex;gap:8px;align-items:end;">
      <div style="width:32px;height:42px;border:2px solid var(--color-teal-600);border-radius:3px;display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:700;color:var(--color-teal-600);">A4</div>
      <div style="width:36px;height:46px;border:2px solid #b45a2a;border-radius:3px;display:flex;align-items:center;justify-content:center;font-size:7px;font-weight:700;color:#b45a2a;">LTR</div>
      <div style="width:30px;height:30px;background:repeating-linear-gradient(0deg,transparent,transparent 5px,#cbd5e1 5px,#cbd5e1 6px),repeating-linear-gradient(90deg,transparent,transparent 5px,#cbd5e1 5px,#cbd5e1 6px);border-radius:3px;"></div>
    </div>`,
    highlights: { buttonText: ['Letter', 'Grid On'] },
  },
]

// ── Highlight helpers ──────────────────────────────────────────

function findButtonByText(text: string): HTMLElement | null {
  const buttons = document.querySelectorAll<HTMLElement>('.design-editor .btn')
  for (const btn of buttons) {
    if (btn.textContent?.trim() === text) return btn
  }
  return null
}

const activeHighlights: HTMLElement[] = []

function clearHighlights() {
  activeHighlights.forEach(el => el.remove())
  activeHighlights.length = 0
}

function highlightElement(el: Element) {
  const rect = el.getBoundingClientRect()
  const ring = document.createElement('div')
  ring.className = 'ob-highlight'
  // z-index must stay below the tour overlay's 10000 — when a highlighted
  // control sits under the card, the instructions must stay readable.
  ring.style.cssText = `
    position:fixed;
    left:${rect.left - 6}px; top:${rect.top - 6}px;
    width:${rect.width + 12}px; height:${rect.height + 12}px;
    border:3px solid #dc2626; border-radius:8px;
    z-index:9999; pointer-events:none;
    box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.3), 0 0 24px rgba(220, 38, 38, 0.25);
    animation: ob-pulse 1.5s ease infinite;
  `
  document.body.appendChild(ring)
  activeHighlights.push(ring)
}

function highlightButton(text: string) {
  const btn = findButtonByText(text)
  if (btn) highlightElement(btn)
}

// ── Main ───────────────────────────────────────────────────────

export function showOnboarding(force = false, actions?: OnboardingActions) {
  if (!force && localStorage.getItem(LS_KEY)) return

  let step = 0

  // ── Overlay ────────────────────────────────────────────────
  const overlay = document.createElement('div')
  // The customize page's outside-click deselection excludes this class —
  // tour clicks must not clear the component the tour highlighted.
  overlay.className = 'ob-overlay'
  overlay.style.cssText = `
    position:fixed; inset:0; z-index:10000;
    display:flex; align-items:flex-start; justify-content:center;
    padding-top:240px;
    pointer-events:none;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;
  `

  // ── Card ───────────────────────────────────────────────────
  const card = document.createElement('div')
  card.style.cssText = `
    background:white; border-radius:12px;
    box-shadow:0 20px 60px rgba(0,0,0,0.3);
    max-width:440px; width:90%;
    padding:32px 32px 24px;
    position:relative;
    pointer-events:auto;
    animation: ob-fade-in 0.25s ease;
  `
  overlay.appendChild(card)

  // Inject animations
  const styleEl = document.createElement('style')
  styleEl.textContent = `
    @keyframes ob-fade-in {
      from { opacity:0; transform:translateY(12px); }
      to { opacity:1; transform:translateY(0); }
    }
    @keyframes ob-pulse {
      0%, 100% { box-shadow: 0 0 0 4px rgba(220,38,38,0.3), 0 0 24px rgba(220,38,38,0.25); }
      50% { box-shadow: 0 0 0 10px rgba(220,38,38,0.15), 0 0 40px rgba(220,38,38,0.15); }
    }
  `
  document.head.appendChild(styleEl)

  function applyStepHighlights() {
    clearHighlights()
    const s = STEPS[step]
    if (!s.highlights || !actions) return

    if (s.highlights.componentType) {
      actions.selectComponentByType(s.highlights.componentType)
    }
    if (s.highlights.buttonText) {
      const texts = Array.isArray(s.highlights.buttonText)
        ? s.highlights.buttonText
        : [s.highlights.buttonText]
      texts.forEach(t => highlightButton(t))
    }
    if (s.highlights.selector) {
      // Delay slightly so the selected component's controls render first
      setTimeout(() => {
        const el = document.querySelector<HTMLElement>(s.highlights!.selector!)
        if (el) highlightElement(el)
      }, 100)
    }
  }

  function clearStepHighlights() {
    clearHighlights()
    actions?.deselectAll()
  }

  function render() {
    const s = STEPS[step]
    const isFirst = step === 0
    const isLast = step === STEPS.length - 1

    card.innerHTML = `
      <div style="text-align:center;margin-bottom:20px;">
        ${s.icon}
      </div>
      <h2 style="font-size:20px;font-weight:700;color:#1e1e1e;margin:0 0 10px;text-align:center;">
        ${s.title}
      </h2>
      <p style="font-size:14px;line-height:1.6;color:#555;margin:0 0 24px;text-align:center;">
        ${s.body}
      </p>
      <!-- Step dots -->
      <div style="display:flex;justify-content:center;gap:6px;margin-bottom:20px;">
        ${STEPS.map(
          (_, i) =>
            `<div style="width:${i === step ? 20 : 8}px;height:8px;border-radius:4px;background:${i === step ? 'var(--color-teal-600)' : '#ddd'};transition:all 0.2s;"></div>`
        ).join('')}
      </div>
      <!-- Buttons -->
      <div style="display:flex;gap:10px;justify-content:center;">
        ${
          !isFirst
            ? `<button id="ob-back" style="
          padding:8px 20px;border-radius:6px;border:1px solid #d1d5db;
          background:#f9fafb;color:#374151;font-size:13px;font-weight:500;
          cursor:pointer;
        ">Back</button>`
            : ''
        }
        <button id="ob-next" style="
          padding:8px 24px;border-radius:6px;border:none;
          background:var(--color-teal-600);color:white;font-size:13px;font-weight:600;
          cursor:pointer;min-width:120px;
        ">${isLast ? 'Start Designing' : isFirst ? "Let's Go" : 'Next'}</button>
      </div>
      <!-- Skip link (a button, not an anchor — anchor clicks are what the
           unsaved-changes guard intercepts as navigation) -->
      <div style="text-align:center;margin-top:14px;">
        <button id="ob-skip" type="button" style="
          font-size:12px;color:#9ca3af;background:none;border:none;cursor:pointer;padding:0;
        ">${isLast ? '' : 'Skip tutorial'}</button>
      </div>
    `

    // Apply highlights for this step (after a short delay so DOM is ready)
    requestAnimationFrame(() => applyStepHighlights())

    // Wire up buttons
    card.querySelector('#ob-next')?.addEventListener('click', () => {
      clearStepHighlights()
      if (isLast) {
        close()
        return
      }
      step++
      render()
    })
    card.querySelector('#ob-back')?.addEventListener('click', () => {
      clearStepHighlights()
      if (step > 0) {
        step--
        render()
      }
    })
    card.querySelector('#ob-skip')?.addEventListener('click', e => {
      e.preventDefault()
      close()
    })
  }

  function close() {
    clearStepHighlights()
    localStorage.setItem(LS_KEY, '1')
    overlay.remove()
    styleEl.remove()
  }

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      close()
      document.removeEventListener('keydown', onKey)
    }
  }
  document.addEventListener('keydown', onKey)

  render()
  document.body.appendChild(overlay)
}
